import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MetadataMgmtApiActions, MetadataMgmtUiActions } from '../actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';
import { MetadataMgmtService } from '../../service/metadata-mgmt.service';
import { MetadataMgmtFacade } from '../../facade/metadata-mgmt.facade';
import { extractDjangoErrors, formatDjangoErrors } from '../../../../shared/utils/django-errors';

@Injectable()
export class MetadataMgmtEffects {
  private readonly actions$ = inject(Actions);
  private readonly toastService = inject(ToastService);
  private readonly metadataMgmtService = inject(MetadataMgmtService);
  private readonly metadataMgmtFacade = inject(MetadataMgmtFacade);

  getSourceTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.GetSourceTypes),
      mergeMap(() =>
        this.metadataMgmtService.getAllSequence().pipe(
          map((sourceTypes) => {
            const typesArray = Array.isArray(sourceTypes) ? sourceTypes : [sourceTypes];
            return MetadataMgmtApiActions.getSourceTypesSuccess({ payload: typesArray });
          }),
          catchError((error) => {
            this.toastService.show(`Error loading source types: ${error.message || 'Unexpected error'}`, 'error');
            return of(MetadataMgmtApiActions.getSourceTypesError({ errors: error }));
          })
        )
      )
    )
  );

  getMetadataKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.GetMetadataKeys),
      mergeMap(({ sourceTypeId, kind }) =>
        this.metadataMgmtService.getMetadataKey(sourceTypeId, kind).pipe(
          map((metadataKeys) => {
            const keysArray = Array.isArray(metadataKeys) ? metadataKeys : metadataKeys ? [metadataKeys] : [];
            return MetadataMgmtApiActions.getMetadataKeysSuccess({ payload: keysArray });
          }),
          catchError((error) => {
            this.toastService.show(`Error loading metadata keys: ${error.message || 'Unexpected error'}`, 'error');
            return of(MetadataMgmtApiActions.getMetadataKeysError({ errors: error }));
          })
        )
      )
    )
  );

  createMetadataKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.CreateMetadataKey),
      mergeMap(({ metadataKey }) =>
        this.metadataMgmtService.createMetadataKey(metadataKey).pipe(
          tap(() => {
            this.toastService.show('Metadata key created successfully', 'success');
          }),
          map((createdMetadataKey) => MetadataMgmtApiActions.createMetadataKeySuccess({ payload: createdMetadataKey })),
          catchError((error) => {
            const message = (error?.error?.detail as string) || formatDjangoErrors(extractDjangoErrors(error));
            this.toastService.show(message, 'error');
            return of(MetadataMgmtApiActions.createMetadataKeyError({ errors: error }));
          })
        )
      )
    )
  );

  updateMetadataKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.UpdateMetadataKey),
      mergeMap(({ metadataKey, id }) =>
        this.metadataMgmtService.updateMetadataKey(metadataKey, id).pipe(
          tap(() => {
            this.toastService.show('Metadata key updated successfully', 'success');
          }),
          map((updatedMetadataKey) => MetadataMgmtApiActions.updateMetadataKeySuccess({ payload: updatedMetadataKey })),
          catchError((error) => {
            const message = (error?.error?.detail as string) || formatDjangoErrors(extractDjangoErrors(error));
            this.toastService.show(message, 'error');
            return of(MetadataMgmtApiActions.updateMetadataKeyError({ errors: error }));
          })
        )
      )
    )
  );

  getMetadataTagKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.GetMetadataTagKeys),
      mergeMap(() =>
        this.metadataMgmtService.getMetadataTagKeys().pipe(
          map((payload) => MetadataMgmtApiActions.getMetadataTagKeysSuccess({ payload })),
          catchError((error) => {
            this.toastService.show(`Error loading metadata tag keys: ${error.message || 'Unexpected error'}`, 'error');
            return of(MetadataMgmtApiActions.getMetadataTagKeysError({ errors: error }));
          })
        )
      )
    )
  );

  uploadMetadataValuesCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.UploadMetadataValuesCsv),
      mergeMap(({ keyId, file, sourceTypeIds, applyToCore }) =>
        this.metadataMgmtService.uploadMetadataValuesCsv(keyId, file, sourceTypeIds, applyToCore).pipe(
          tap((result) => {
            if (result.success) {
              this.toastService.show(`${result.success_count} values uploaded`, 'success');
            } else if (result.success_count > 0) {
              this.toastService.show(
                `Partial success: ${result.success_count} uploaded, ${result.error_count} failed`,
                'warning'
              );
            } else {
              this.toastService.show(`Upload failed: ${result.error_count} errors`, 'error');
            }
            // refresh the inline editor's value list in the SAME scope the upload
            // was made in to show the new values and allow the user to edit/save them.
            if (result.success_count > 0) {
              const refreshScope = applyToCore ? undefined : sourceTypeIds[0];
              this.metadataMgmtFacade.getMetadataKeys(refreshScope);
            }
          }),
          map((payload) => MetadataMgmtApiActions.uploadMetadataValuesCsvSuccess({ payload })),
          catchError((error) => {
            this.toastService.show(`${error?.error?.detail || error?.message || 'Upload failed'}`, 'error');
            return of(MetadataMgmtApiActions.uploadMetadataValuesCsvError({ errors: error }));
          })
        )
      )
    )
  );

  deleteMetadataKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MetadataMgmtUiActions.DeleteMetadataKey),
      mergeMap(({ metadataKeyId }) =>
        this.metadataMgmtService.deleteMetadataKey(metadataKeyId).pipe(
          tap(() => {
            this.toastService.show('Metadata key deleted successfully', 'success');
          }),
          map(() => MetadataMgmtApiActions.deleteMetadataKeySuccess({ metadataKeyId })),
          catchError((error) => {
            const message = (error?.error?.detail as string) || formatDjangoErrors(extractDjangoErrors(error));
            this.toastService.show(message, 'error');
            return of(MetadataMgmtApiActions.deleteMetadataKeyError({ errors: error }));
          })
        )
      )
    )
  );
}
